import React, { useState } from 'react'
import { useAuth0 } from '@auth0/auth0-react'
import { useNavigate } from 'react-router-dom'
import * as Sentry from '@sentry/react'

const { logger } = Sentry;
import { deleteAccount } from '../../services/api'
import './DeleteAccountModal.scss'

interface DeleteAccountModalProps {
  show: boolean
  onHide: () => void
}

function DeleteAccountModal({ show, onHide }: DeleteAccountModalProps) {
  const { getAccessTokenSilently } = useAuth0()
  const navigate = useNavigate()
  const [isDeleting, setIsDeleting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  if (!show) return null

  const handleDelete = async () => {
    setIsDeleting(true)
    setError(null)

    try {
      await deleteAccount(getAccessTokenSilently)
      // Account deletion initiated successfully
      // Close the modal and redirect to dashboard
      onHide()
      navigate('/dashboard')
    } catch (err) {
      logger.error('Error deleting account', {
        error: err instanceof Error ? err.message : String(err),
      });
      const errorMessage = err instanceof Error ? err.message : 'Failed to delete account. Please try again.'
      setError(errorMessage)
      setIsDeleting(false)
    }
  }

  return (
    <div className="modal-overlay">
      <div className="modal-content delete-account-modal">
        <div className="modal-header">
          <h5 className="modal-title">Delete Account</h5>
          <button
            type="button"
            className="btn-close"
            onClick={onHide}
            aria-label="Close"
            disabled={isDeleting}
          />
        </div>
        <div className="modal-body">
          <div className="alert alert-warning" role="alert">
            <strong>Warning:</strong> This action cannot be easily undone.
          </div>
          <p>
            Are you sure you want to delete your account? This will:
          </p>
          <ul>
            <li>Permanently delete all your stored data</li>
            <li>Remove all remaining credits</li>
            <li>Delete all job postings and activities</li>
          </ul>
          <p className="text-muted">
            Your account will be scheduled for deletion in 30 days. You can undo this action during that time.
          </p>
          {error && (
            <div className="alert alert-danger mt-3" role="alert">
              {error}
            </div>
          )}
        </div>
        <div className="modal-footer">
          <button
            type="button"
            className="btn btn-secondary"
            onClick={onHide}
            disabled={isDeleting}
          >
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-danger"
            onClick={handleDelete}
            disabled={isDeleting}
          >
            {isDeleting ? 'Deleting...' : 'Delete Account'}
          </button>
        </div>
      </div>
    </div>
  )
}

export default DeleteAccountModal
